/**
 * generatePreProcessJsonMetadata_MissingInfo.ts - Missing info collection for metadata generation
 * Port of C# GeneratePreProcessJsonMetadata_MissingInfo.cs
 * Pure ES module - works in both Node.js and browser via bundler
 */

import { MissingJsonKeyInfo, MissingMetaInfo, MissingPlaceholderInfo, MissingSlotholderInfo } from '../../model/appSiteMetadata.js';
import type { TemplateInfo, SlotInfo } from '../../model/appSiteMetadata.js';
import { isJsonKeyDuplicate, isPlaceholderDuplicate, isSlotholderDuplicate } from './generatePreProcessJsonMetadata_DuplicateChecks.js';

/**
 * Collects all unresolved items from the template hierarchy into a consolidated MissingMetaInfo structure
 */
export function collectMissingMetaInfo(rootTemplate: TemplateInfo, _appSiteName: string): MissingMetaInfo {
    const missingInfo = new MissingMetaInfo();
    collectMissingMetaInfoRecursive(rootTemplate, missingInfo);
    return missingInfo;
}

/**
 * Recursively collects missing info from template and all its components
 */
function collectMissingMetaInfoRecursive(template: TemplateInfo, missingInfo: MissingMetaInfo): void {
    // Collect NonPlaceholders
    for (const placeholder of (template.nonPlaceholders || [])) {
        if (!isPlaceholderDuplicate(missingInfo.nonPlaceholders, placeholder, template.templateName, template.templateAppSite)) {
            const info = new MissingPlaceholderInfo();
            info.placeholderName = placeholder;
            info.templateName = template.templateName;
            info.templateAppSite = template.templateAppSite;
            missingInfo.nonPlaceholders.push(info);
        }
    }

    // Collect NonSlotholders
    for (const slotholder of (template.nonSlotholders || [])) {
        if (!isSlotholderDuplicate(missingInfo.nonSlotholders, slotholder.name, template.templateName, template.templateAppSite)) {
            const info = new MissingSlotholderInfo();
            info.slotholderName = slotholder.name;
            info.templateName = template.templateName;
            info.templateAppSite = template.templateAppSite;
            missingInfo.nonSlotholders.push(info);
        }
    }

    // Collect NonJsonKeys
    for (const jsonKey of (template.nonJsonKeys || [])) {
        if (!isJsonKeyDuplicate(missingInfo.nonJsonKeys, jsonKey, template.templateName, template.templateAppSite)) {
            const info = new MissingJsonKeyInfo();
            info.jsonKey = jsonKey;
            info.templateName = template.templateName;
            info.templateAppSite = template.templateAppSite;
            missingInfo.nonJsonKeys.push(info);
        }
    }

    // Collect NonJsonArrayKeys
    for (const jsonArrayKey of (template.nonJsonArrayKeys || [])) {
        if (!isJsonKeyDuplicate(missingInfo.nonJsonArrayKeys, jsonArrayKey, template.templateName, template.templateAppSite)) {
            const info = new MissingJsonKeyInfo();
            info.jsonKey = jsonArrayKey;
            info.templateName = template.templateName;
            info.templateAppSite = template.templateAppSite;
            missingInfo.nonJsonArrayKeys.push(info);
        }
    }

    // Recursively collect from child components
    for (const component of (template.components || [])) {
        collectMissingMetaInfoRecursive(component, missingInfo);
    }

    // Recursively collect from slotholders
    for (const slotholder of (template.slotholders || [])) {
        for (const slot of (slotholder.slots || [])) {
            for (const nestedSlotholder of (slot.slotholders || [])) {
                collectMissingMetaInfoRecursiveFromSlot(nestedSlotholder, missingInfo);
            }
        }
    }
}

/**
 * Recursively collects missing info from slot structures
 */
function collectMissingMetaInfoRecursiveFromSlot(slotholder: SlotInfo, missingInfo: MissingMetaInfo): void {
    for (const slot of (slotholder.slots || [])) {
        for (const nestedSlotholder of (slot.slotholders || [])) {
            collectMissingMetaInfoRecursiveFromSlot(nestedSlotholder, missingInfo);
        }
    }
}
